import TranslationService, {
    concatenateContextWithKey,
    ITranslationService
} from '@services/TranslationService';
import i18n from 'i18next';
import {
    keys,
    mapValues
} from 'lodash';
import {
    action,
    observable
} from 'mobx';

export const useTranslation = (context: string = '') => {
    const translationService: ITranslationService = TranslationService.getInstance();
    return {
        get: (key: string) => translationService.translate(
            concatenateContextWithKey(key, context)
        )
    };
};

/**
 * Returns a cached observable translation map
 * @param translationService
 * @param translations
 * @param translationsContext
 */
export function cacheTranslations<TranslationMap extends Record<string, string>>(
    translationService: ITranslationService,
    translations: TranslationMap,
    translationsContext: string = ''
): { [P in keyof TranslationMap]: string } {
    // this could be lazily evaluated in the future to optimise
    const translatedValues = mapValues(translations, translationKey => translationService.translate(
        concatenateContextWithKey(translationKey, translationsContext)
    ));

    const observableTranslations = observable({
        ...translatedValues,
        respondToLanguageChanged() {
            for (const key of keys(translations)) {
                // @ts-ignore - no reasonable way to cast the key to index the deep observable
                observableTranslations[key] = translationService.translate(concatenateContextWithKey(key, translationsContext));
            }
        }
    }, {
        respondToLanguageChanged: action.bound
    });

    i18n.on('languageChanged', () => observableTranslations.respondToLanguageChanged());

    return observableTranslations as unknown as { [P in keyof TranslationMap]: string };
}

/**
 * Returns a cached observable translation list map
 * @param translationService
 * @param translations
 * @param translationsContext
 */
export function cacheTranslationLists<TranslationMap extends Record<string, string>>(
    translationService: ITranslationService,
    translations: TranslationMap,
    translationsContext: string = ''
): { [P in keyof TranslationMap]: string[] } {
    // this could be lazily evaluated in the future to optimise
    const translatedValues = mapValues<TranslationMap, string[]>(translations, translationKey => {
        const list = translationService.translateObject(
            concatenateContextWithKey(translationKey, translationsContext)
        ) as string[];

        if (!Array.isArray(list)) {
            return [];
        }

        return list;
    });

    const observableTranslations = observable({
        ...translatedValues,
        respondToLanguageChanged() {
            for (const key of keys(translations)) {
                const list = translationService.translateObject(concatenateContextWithKey(key, translationsContext));

                if (!Array.isArray(list)) {
                    // @ts-ignore - no reasonable way to cast the key to index the deep observable
                    observableTranslations[key] = [];
                    continue;
                }

                // @ts-ignore - no reasonable way to cast the key to index the deep observable
                observableTranslations[key] = list;
            }
        }
    }, {
        respondToLanguageChanged: action.bound
    });

    i18n.on('languageChanged', () => observableTranslations.respondToLanguageChanged());

    return observableTranslations as unknown as { [P in keyof TranslationMap]: string[] };
}



// WEBPACK FOOTER //
// ./src/utils/cache-translations.ts